import { VelocityVariable } from '../domain/enrichment/EnrichmentDomain';
import { FormicaTimePickerDate } from '../components/custom/custom-time-picker/TimePickerDomain';
import DatePickerUtil from '../components/custom/custom-time-picker/DatePickerUtil';
import EnrichmentClient from '../client/enrichment/EnrichmentClient';
import GatewayContainer from '../config/GatewayContainer';
import GatewayNames from '../constant/GatewayNames';
import MessageUtil from '../util/MessageUtil';
import _ from 'lodash';
import { EnrichedComponentCategory } from '../domain/category/CategoryOperationDomains';
import ComponentName from '../constant/ComponentName';
import FolderClient from '../client/folder/FolderClient';
import { Syntax } from '@trlogic/ui-pack';
import { dispatch } from '../redux/store';
import { openBulkActionDialogFromDependency } from '../redux/actions/BulkActionActions';
import LanguageUtil from '../util/LanguageUtil';
import DeployErrorUtil from '../util/DeployErrorUtil';
import React from 'react';
import ExpressionBridge from '../util/ExpressionBridge';
import { changeStreamDirty } from '../redux/actions/StreamActions';
import { changeVelocityVariableDirty } from '../redux/actions/VelocityVariableActions';

class VelocityVariableService {
  private readonly folderClient: FolderClient = GatewayContainer.getInstance().get(GatewayNames.FOLDER_CLIENT);
  private readonly enrichmentClient: EnrichmentClient = GatewayContainer.getInstance().get(GatewayNames.ENRICHMENT_CLIENT);

  private readonly expressionBridge: ExpressionBridge;

  constructor(expressionBridge: ExpressionBridge) {
    this.expressionBridge = expressionBridge;
  }

  public async save(_variable: VelocityVariable, isUpdate: boolean, folder: EnrichedComponentCategory): Promise<boolean> {
    const variable = _.cloneDeep(_variable);
    const parsedVariable = await this.parseVariable(variable);
    if (this.isVariableValid(parsedVariable)) {
      try {
        const exist = await this.enrichmentClient.exists(variable.name);
        if (exist && !isUpdate) {
          MessageUtil.alreadyExist(
            LanguageUtil.get(
              { id: 'messages.warning.alreadyExist', defaultMessage: `${variable.name} already exist. ${exist.type}:${exist.id} is using` },
              { 0: `${variable.name}`, 1: `${exist.type}`, 2: `${exist.id}` },
            ),
          );
          return false;
        }
        const dto = { id: variable.name, object: parsedVariable };
        await this.enrichmentClient.save(dto);
        if (!isUpdate && folder) {
          await this.folderClient.addItemToCategory(ComponentName.ENRICHMENT, folder.id, variable.name);
        }
        MessageUtil.success({
          summary: LanguageUtil.get({ id: 'common.label.velocity', defaultMessage: 'Velocity' }),
          detail: LanguageUtil.get({ id: 'messages.success.velocityVariableSaved', defaultMessage: 'Velocity variable saved' }),
        });
        dispatch(changeVelocityVariableDirty(false));
        return true;
      } catch (e) {
        MessageUtil.error({
          summary: LanguageUtil.get({ id: 'common.label.velocity', defaultMessage: 'Velocity' }),
          detail: LanguageUtil.get({ id: 'messages.warning.fillFields', defaultMessage: 'Please fill all fields.' }),
        });
        return false;
      }
    } else {
      MessageUtil.warning({
        content: LanguageUtil.get({ id: 'messages.warning.checkAllFields', defaultMessage: 'Please check all fields.' }),
      });
    }
    return false;
  }

  public async delete(id: string): Promise<boolean> {
    try {
      const isExist = await this.exist(id);
      if (isExist) {
        const response: any = await this.enrichmentClient.remove(id);
        if (response.code) {
          return false;
        }
        MessageUtil.deleteMessage(`Velocity ${id} deleted`);
        return true;
      }
      MessageUtil.success({
        summary: LanguageUtil.get({ id: 'common.label.velocityVariable', defaultMessage: 'Velocity Variable' }),
        detail: (
          <div>
            <b>{id}</b> {LanguageUtil.get({ id: 'messages.notRemove', defaultMessage: 'not remove' })}
          </div>
        ),
      });
      return false;
    } catch (e: any) {
      if (DeployErrorUtil.show(e, LanguageUtil.get({ id: 'common.label.velocity', defaultMessage: 'Velocity' }))) {
        return false;
      }
      const dependencies: any = e.response.data;
      if (dependencies?.message == 'Dependency Exception') {
        dispatch(openBulkActionDialogFromDependency(dependencies, ComponentName.VELOCITY));
        return false;
      }
      MessageUtil.error({
        content: 'Error when deleting velocity',
      });
      return false;
    }
  }

  public async deploy(variable: VelocityVariable): Promise<boolean> {
    const parsedVariable = await this.parseVariable(_.cloneDeep(variable));
    if (!this.isVariableValid(parsedVariable)) {
      MessageUtil.warning({
        content: LanguageUtil.get({ id: 'messages.warning.checkAllFields', defaultMessage: 'Please check all fields.' }),
      });
      return false;
    }
    try {
      await this.enrichmentClient.deploy(variable.name);
      MessageUtil.success({
        summary: LanguageUtil.get({ id: 'common.label.velocity', defaultMessage: 'Velocity' }),
        detail: LanguageUtil.get({ id: 'messages.success.veloDeployed', defaultMessage: 'Velocity Deployed.' }),
      });
      return true;
    } catch (error: unknown) {
      if (DeployErrorUtil.show(error, LanguageUtil.get({ id: 'common.label.velocity', defaultMessage: 'Velocity' }))) {
        return false;
      }
      MessageUtil.error({
        summary: LanguageUtil.get({ id: 'common.label.velocity', defaultMessage: 'Velocity' }),
        detail: LanguageUtil.get({ id: 'messages.error.veloDeploying', defaultMessage: 'Error when deploying.' }),
      });
      return false;
    }
  }

  public async undeploy(id: string): Promise<void> {
    try {
      await this.enrichmentClient.undeploy(id);
      MessageUtil.success({
        summary: LanguageUtil.get({ id: 'common.label.velocity', defaultMessage: 'Velocity' }),
        detail: (
          <div>
            <b>{id}</b> {LanguageUtil.get({ id: 'messages.undeploy', defaultMessage: `undeployed` })}
          </div>
        ),
      });
    } catch (e: any) {
      if (DeployErrorUtil.show(e, LanguageUtil.get({ id: 'common.label.velocity', defaultMessage: 'Velocity' }))) {
        return;
      }
      const dependencies: any = e.response.data;
      if (dependencies?.message == 'Dependency Exception') {
        dispatch(openBulkActionDialogFromDependency(dependencies, ComponentName.VELOCITY));
      }
      MessageUtil.error({ content: LanguageUtil.get({ id: 'messages.error.veloUndeploying', defaultMessage: 'Error while undeploying velocity.' }) });
    }
  }

  public exist(id: string): Promise<{ type: any; id: string }> {
    return this.enrichmentClient.exists(id);
  }

  private async parseVariable(variable: VelocityVariable) {
    variable.startDate = this.refreshDateValue(variable.startDate);
    variable.endDate = this.refreshDateValue(variable.endDate);
    if (variable && variable.where && variable.groupBy && variable.select) {
      return {
        ...variable,
        where: await this.parseExpression(variable.where),
        groupBy: await this.parseExpression(variable.groupBy),
        select: await this.parseExpression(variable.select),
      };
    }
    return variable;
  }

  private refreshDateValue(date: FormicaTimePickerDate): FormicaTimePickerDate {
    switch (date.type) {
      case 'Now':
        return { ...date, value: new Date() };
      case 'Quick': {
        const refreshed = this.recalculateQuickValue(date.label);
        return refreshed != null ? { ...date, value: refreshed } : date;
      }
      default:
        return date;
    }
  }

  private recalculateQuickValue(label: string | undefined): Date | null {
    if (!label) return null;
    const parts = label.split(' ');
    if (parts.length !== 3) return null;
    const duration = Number(parts[1]);
    if (isNaN(duration) || duration <= 0) return null;
    const unit = parts[2].toLowerCase().replace(/s$/, '');
    return DatePickerUtil.calculateQuickValue(duration, unit);
  }

  private async parseExpression(syntax: Syntax) {
    const iterateSyntax = (syntax: Syntax): Syntax => {
      return {
        id: syntax.id,
        value: syntax.value,
        children: syntax.children?.map((c) => iterateSyntax(c)),
      };
    };
    return iterateSyntax(await this.expressionBridge.stop(syntax));
  }

  private isVariableValid(variable: VelocityVariable): boolean {
    if (
      variable.startDate == undefined ||
      Object.keys(variable.startDate).length == 0 ||
      (variable.startDate.type !== 'Relative' && variable.startDate.value == undefined)
    ) {
      return false;
    }

    if (
      variable.endDate == undefined ||
      Object.keys(variable.endDate).length == 0 ||
      (variable.endDate.type !== 'Relative' && variable.endDate.value == undefined)
    ) {
      return false;
    }
    return (
      this.hasText(variable.name) &&
      this.hasText(variable.transaction) &&
      this.hasText(variable.stream) &&
      this.hasText(variable.function) &&
      this.hasRequiredExpressions(variable)
    );
  }

  private hasRequiredExpressions(variable: VelocityVariable): boolean {
    if (this.isExpressionMissing(variable.groupBy)) {
      return false;
    }
    switch (variable.function) {
      case 'count':
        return true;
      default:
        return !this.isExpressionMissing(variable.select);
    }
  }

  private isExpressionMissing(syntax: Syntax | undefined): boolean {
    if (syntax == undefined) {
      return true;
    }
    switch (syntax.value.type) {
      case 'Empty':
        return true;
      case 'TokenGroup':
      case 'FunctionArg':
      case 'Logic':
        return this.areChildrenMissing(syntax.children);
      default:
        return false;
    }
  }

  private areChildrenMissing(children: Syntax[] | undefined): boolean {
    if (children == undefined || children.length === 0) {
      return true;
    }
    return children.every((child) => this.isExpressionMissing(child));
  }

  private hasText(value: string | undefined): boolean {
    return value != undefined && value.trim().length > 0;
  }
}

export default VelocityVariableService;
